import { mapFromApprovalDetail, mapFromApprovalRequest, mapToDecideApproval } from 'mappings/approvalMapping';
import { ApprovalDetail, ApprovalRequest } from 'models/approvals/approval';
import { ApprovalDetailDTO, ApprovalRequestDTO } from 'dto/approvals/approvalDTO';
import AppError from 'utils/appError';
import http from './httpService';

//
// The human approvals of paused tool calls (approvals/*, ApprovalController). An approval is visible to its approvers,
// its requester and a Security Reviewer: GET approvals/{id} is 404 for anyone else and in another organization, a
// decision IP0002. A refused decision is an AppError with an APPR code: APPR0001 (no comment on a reject, too long),
// APPR0002 (the requester of a group approval), APPR0003 (no longer pending).
//

//the pending approvals the caller may decide now, oldest first
export async function apiGetApprovalsToDecide(
  accessToken: string,
  abortSignal?: AbortSignal,
): Promise<ApprovalRequest[]> {
  try {
    const ar = await http.get<ApprovalRequestDTO[]>(
      '/approvals/mine',
      http.getRequestConfig(accessToken, undefined, abortSignal),
    );

    return (ar.data ?? []).map(mapFromApprovalRequest);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//the approvals of the caller's own runs, newest first
export async function apiGetRequestedApprovals(
  accessToken: string,
  skip: number,
  take: number,
  abortSignal?: AbortSignal,
): Promise<ApprovalRequest[]> {
  try {
    const ar = await http.get<ApprovalRequestDTO[]>(
      `/approvals/requested?skip=${skip}&take=${take}`,
      http.getRequestConfig(accessToken, undefined, abortSignal),
    );

    return (ar.data ?? []).map(mapFromApprovalRequest);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//one approval with the display of the paused call; undefined on a 404 (unknown, not visible to the caller, or of
//another organization)
export async function apiGetApproval(
  accessToken: string,
  approvalRequestId: string,
  abortSignal?: AbortSignal,
): Promise<ApprovalDetail | undefined> {
  try {
    const ar = await http.get<ApprovalDetailDTO>(
      `/approvals/${approvalRequestId}`,
      http.getRequestConfig(accessToken, undefined, abortSignal),
    );

    return mapFromApprovalDetail(ar.data);
  } catch (err) {
    const appError = AppError.fromApiError(err);
    if (appError.code === '404') {
      return undefined;
    }
    throw appError;
  }
}

//approves the paused call (comment optional); the execution resumes on the API's worker
export async function apiApproveApproval(
  accessToken: string,
  approvalRequestId: string,
  comment: string | undefined,
): Promise<ApprovalRequest> {
  try {
    const ar = await http.post<ApprovalRequestDTO>(
      `/approvals/${approvalRequestId}/approve`,
      mapToDecideApproval(comment),
      http.getRequestConfig(accessToken),
    );

    return mapFromApprovalRequest(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//rejects the paused call with a required comment; the execution ends Failed (APPR0101)
export async function apiRejectApproval(
  accessToken: string,
  approvalRequestId: string,
  comment: string,
): Promise<ApprovalRequest> {
  try {
    const ar = await http.post<ApprovalRequestDTO>(
      `/approvals/${approvalRequestId}/reject`,
      mapToDecideApproval(comment),
      http.getRequestConfig(accessToken),
    );

    return mapFromApprovalRequest(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}
